import isNull from "lodash/isNull";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { bugNotif } from "../../api/bugsnag";
import { getErrorMsg } from "../../../Common/utils/common";
import { getSubscriptionListStripe } from "../../services/subscription";
import { orderBy } from "lodash";
import useWebReferralStore from "./store";
import { getRefferedBonusEntries } from "../../utils/promo";
import { getWlConfig } from "../../services/config";
import { ReferralConfig } from "../../../Common/models/config";

export const useSubscriptionsProvider = () => {
  const { setSelectedSubscription, setSubscriptionList } =
    useWebReferralStore();

  // GET OPTIONS FOR SUBSCRIPTION
  const { data: subscriptionList, error: subscriptionListError } = useQuery({
    queryKey: ["GET_SUBSCRIPTION_WEB_SIGNUP"],
    queryFn: async () => {
      const resp = await getSubscriptionListStripe();
      return orderBy(resp, (sub) => sub.price, "asc");
    },
    refetchOnWindowFocus: false,
  });

  // To handle subscription list get error
  useEffect(() => {
    if (!isNull(subscriptionListError)) {
      bugNotif(
        "StepEmailSetup - getSubscriptions",
        getErrorMsg(subscriptionListError).rawErrorMsg
      );
    }
  }, [subscriptionListError]);

  // To handle selected subscription.
  useEffect(() => {
    if (subscriptionList !== undefined) {
      setSubscriptionList(subscriptionList);
      setSelectedSubscription(
        subscriptionList.length > 0 ? subscriptionList[0] : null
      );
    }
  }, [subscriptionList]);
};

export const useGetReferralEntries = () => {
  const [referralParam, setReferralParam] = useState<
    | { referredByMemberId: string; referralBonusLookUpKey: string | null }
    | undefined
  >(undefined);
  const [referralConfig, setReferralConfig] = useState<
    ReferralConfig | undefined
  >(undefined);
  const { selectedSubscription, setReferredBonusEntries } =
    useWebReferralStore();

  const { data, error } = useQuery({
    queryKey: ["WLCONFIG", referralParam],
    queryFn: async () => {
      if (referralParam) {
        const resp = await getWlConfig();
        return resp;
      }
    },
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    if (!isNull(error)) {
      bugNotif(
        "useGetReferralEntries - getWLConfigProvider",
        getErrorMsg(error).rawErrorMsg
      );
    }
  }, [error]);

  useEffect(() => {
    const configs = data?.referralConfig;
    const lookupKey = referralParam?.referralBonusLookUpKey;

    if (!Array.isArray(configs) || configs.length === 0) return;

    const referralConfig =
      configs.find(({ lookUpKey }) => lookUpKey === lookupKey) ??
      configs.find(({ isDefault }) => isDefault) ??
      configs[0];

    setReferralConfig(referralConfig);
  }, [data, referralParam]);

  useEffect(() => {
    if (
      referralConfig !== undefined &&
      selectedSubscription !== undefined &&
      !isNull(selectedSubscription)
    ) {
      const referedBonusEntries = getRefferedBonusEntries(
        referralConfig.referredBonus,
        selectedSubscription.entriesPerMonth
      );
      const twoWayBonusEntries = getRefferedBonusEntries(
        referralConfig.twoWayBonus,
        selectedSubscription.entriesPerMonth
      );

      setReferredBonusEntries({
        referedBonusEntries,
        twoWayBonusEntries,
        referedTotalEntries:
          referedBonusEntries.value + twoWayBonusEntries.value,
      });
    } else {
      setReferredBonusEntries(undefined);
    }
  }, [referralConfig, selectedSubscription]);

  useEffect(() => {
    const referredByMemberId = new URLSearchParams(window.location.search).get(
      "referredByMemberId"
    );

    const referralBonusLookUpKey = new URLSearchParams(
      window.location.search
    ).get("referralBonusLookUpKey");

    if (!isNull(referredByMemberId)) {
      setReferralParam({
        referredByMemberId,
        referralBonusLookUpKey,
      });
    }
  }, []);
};
